import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import Iconify from "src/components/Iconify";
import useAuth from "src/hooks/useAuth";
import Transition from "src/utils/dialog-animation";

import socialImage from "src/images/social-juenesse.png";

const HeaderWrapper = styled(Box)(({ theme }) => ({
  position: "relative",
  padding: theme.spacing(4, 3, 3, 3),
  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 50%, ${alpha(theme.palette.primary.light, 0.8)} 100%)`,
  color: theme.palette.common.white,
  borderTopLeftRadius: theme.shape.borderRadius * 2,
  borderTopRightRadius: theme.shape.borderRadius * 2,
  textAlign: "center",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: -40,
    right: -40,
    width: 140,
    height: 140,
    borderRadius: "50%",
    background: "rgba(255, 255, 255, 0.1)",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: -30,
    left: -30,
    width: 100,
    height: 100,
    borderRadius: "50%",
    background: "rgba(255, 255, 255, 0.08)",
  },
}));

const SocialCard = styled(Paper)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 1.5,
  border: `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
  boxShadow: theme.customShadows.z8,
  transition: theme.transitions.create(["transform", "box-shadow", "border-color"], {
    duration: theme.transitions.duration.shorter,
  }),
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: theme.customShadows.z16,
    borderColor: alpha(theme.palette.primary.main, 0.3),
  },
}));

const SocialLinksDialog = () => {
  const { t } = useTranslation();
  const { user, isSocialLinksOpen, closeSocialLinks } = useAuth();
  const socialLinks = user?.socialLinks || user?.social_links || [];

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={Boolean(isSocialLinksOpen)}
      onClose={closeSocialLinks}
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: (theme) => theme.customShadows.z24,
        },
      }}
    >
      <HeaderWrapper>
        <IconButton
          onClick={closeSocialLinks}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "common.white",
            bgcolor: "rgba(255, 255, 255, 0.15)",
            "&:hover": {
              bgcolor: "rgba(255, 255, 255, 0.3)",
            },
          }}
          size="small"
        >
          <Iconify icon="eva:close-fill" width={20} height={20} />
        </IconButton>

        <Avatar
          src={socialImage}
          alt="Social Links"
          sx={{
            width: 130,
            height: 130,
            mx: "auto",
            mb: 0.5,
          }}
          imgProps={{
            style: {
              objectFit: "contain",
            },
          }}
        />

        <Typography variant="h5" fontWeight={700}>
          {t("social_links_dialog.title")}
        </Typography>

        <Typography
          variant="body2"
          sx={{ opacity: 0.85, mt: 0.5, px: 2, fontSize: "0.85rem" }}
        >
          {t("social_links_dialog.subtitle")}
        </Typography>
      </HeaderWrapper>

      <DialogContent sx={{ p: 3, bgcolor: "background.neutral" }}>
        <Stack spacing={2}>
          {socialLinks.length > 0 ? (
            socialLinks.map((item) => (
              <SocialCard key={item.id || item.name}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar
                    src={item.icon}
                    alt={item.name}
                    variant="rounded"
                    sx={{
                      width: 44,
                      height: 44,
                      bgcolor: "background.paper",
                      p: 0.5,
                      boxShadow: (theme) => theme.customShadows.z4,
                    }}
                  >
                    <Iconify icon="eva:share-fill" width={24} height={24} />
                  </Avatar>

                  <Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{ textTransform: "capitalize" }}
                    >
                      {item.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        display: "block",
                        maxWidth: 160,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.link}
                    </Typography>
                  </Box>
                </Stack>

                <Button
                  variant="contained"
                  size="small"
                  startIcon={
                    <Iconify icon="eva:external-link-fill" width={16} height={16} />
                  }
                  onClick={() => {
                    if (item.link) {
                      window.open(item.link, "_blank", "noopener,noreferrer");
                    }
                  }}
                  sx={{
                    borderRadius: 1.5,
                    px: 2,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  {t("social_links_dialog.join")}
                </Button>
              </SocialCard>
            ))
          ) : (
            <Paper
              sx={{
                p: 3,
                textAlign: "center",
                borderRadius: 2,
                bgcolor: "background.paper",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t("social_links_dialog.no_links")}
              </Typography>
            </Paper>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default SocialLinksDialog;
